// Shows optimized bullets with every word the AI added highlighted, by
// comparing each optimized bullet with its closest original bullet.
// Display only: the saved data and the downloaded resume are not changed.

const HIGHLIGHT_STYLE = {
  backgroundColor: "#fff3b0",
  color: "#5c4a00",
  borderBottom: "2px solid #f0c000",
  borderRadius: "3px",
  padding: "0 2px",
};

// Below this share of common words, an optimized bullet counts as brand new.
const NEW_BULLET_THRESHOLD = 0.3;

const tokenize = (text) => String(text || "").trim().split(/\s+/).filter(Boolean);

// Lowercase and strip surrounding punctuation so "IAM," matches "IAM".
const normalize = (word) => word.toLowerCase().replace(/^[^\w+#]+|[^\w+#%]+$/g, "");

function similarity(wordsA, wordsB) {
  const setA = new Set(wordsA.filter(Boolean));
  const setB = new Set(wordsB.filter(Boolean));
  if (!setA.size || !setB.size) return 0;
  let shared = 0;
  for (const w of setB) if (setA.has(w)) shared++;
  return shared / Math.max(setA.size, setB.size);
}

// Marks each optimized word as added (true) or kept from the original (false),
// using the longest common subsequence of words.
function markAddedWords(originalWords, optimizedWords) {
  const n = originalWords.length;
  const m = optimizedWords.length;
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));

  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] =
        originalWords[i] && originalWords[i] === optimizedWords[j]
          ? dp[i + 1][j + 1] + 1
          : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const added = new Array(m).fill(true);
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (originalWords[i] && originalWords[i] === optimizedWords[j]) {
      added[j] = false;
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      i++;
    } else {
      j++;
    }
  }
  return added;
}

// Splits one optimized bullet into plain and highlighted segments.
function buildSegments(optimizedBullet, originalBullets) {
  const words = tokenize(optimizedBullet);
  const normWords = words.map(normalize);

  // Find the original bullet this one was rewritten from.
  let bestOriginal = null;
  let bestScore = 0;
  for (const original of originalBullets) {
    const score = similarity(tokenize(original).map(normalize), normWords);
    if (score > bestScore) {
      bestScore = score;
      bestOriginal = original;
    }
  }

  const added =
    bestOriginal && bestScore >= NEW_BULLET_THRESHOLD
      ? markAddedWords(tokenize(bestOriginal).map(normalize), normWords)
      : new Array(words.length).fill(true); // brand-new bullet

  // Punctuation-only tokens ("—", "&") are never highlighted on their own.
  const flags = words.map((_, k) => added[k] && normWords[k] !== "");

  // Merge neighboring words with the same flag into one segment.
  const segments = [];
  words.forEach((word, k) => {
    const last = segments[segments.length - 1];
    if (last && last.added === flags[k]) {
      last.text += ` ${word}`;
    } else {
      segments.push({ text: word, added: flags[k] });
    }
  });
  return segments;
}

export default function HighlightedBulletList({ originalBullets = [], optimizedBullets = [], showHighlight = true }) {
  if (!optimizedBullets.length) {
    return <p style={{ fontSize: "13px", color: "#8a8a8a", margin: 0 }}>No bullet points.</p>;
  }

  return (
    <ul style={{ fontSize: "13px", margin: 0, paddingLeft: "18px" }}>
      {optimizedBullets.map((bullet, bi) => {
        const segments = buildSegments(bullet, originalBullets);
        return (
          <li key={bi} style={{ marginBottom: "6px", lineHeight: "1.6" }}>
            {segments.map((seg, si) => (
              <span key={si}>
                {si > 0 ? " " : ""}
                {seg.added && showHighlight ? (
                  <mark style={HIGHLIGHT_STYLE} title="Added by AI">
                    {seg.text}
                  </mark>
                ) : (
                  seg.text
                )}
              </span>
            ))}
          </li>
        );
      })}
    </ul>
  );
}

export function AiChangesLegend() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", color: "#5a5a5a", margin: "0 0 12px", flexWrap: "wrap" }}>
      <mark style={HIGHLIGHT_STYLE}>Highlighted text</mark>
      <span>= words added by AI. Highlights are shown here only and are not part of the downloaded resume.</span>
    </div>
  );
}